import { ChangeEvent, useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import {
  BuyXPayY,
  PaymentOptionInfoDto,
  ProgressiveDiscount,
  PromotionRequestDto,
  Reward,
} from '@tiendanube/common';
import { PromotionTypes } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { ShowByTag } from 'App/components';
import {
  CUSTOM_PROMOTION_LABEL_ENABLED,
  PROMOTIONS_GRANULAR_COMBINATION_TAG,
} from 'App/featuresFlags';
import {
  ApplyToCard,
  DiscountTypeCard,
  NameCard,
  LimitsCard,
  CustomLabelCard,
  GranularDiscountCombination,
} from './components';
import { CrossSelling } from './components/CrossSelling';
import { ScopeValuesInterface } from '../../pages/types';
import { usePromotionTypesQuery } from '../../promotionsApi';
import { isPercentageOffPromotion } from '../../utils/promotionUtils';

export type PromotionFormValues = PromotionRequestDto & {
  customLabelEnabled?: boolean;
};

export interface PromotionFormProps {
  mode: 'edit' | 'add';
  values: PromotionFormValues;
  errors: Partial<Record<string, string>>;
  isExternal: boolean;
  promotionId?: string;
  setFieldValue: (name: string, value: any) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onChangeSelect: (e: ChangeEvent<HTMLSelectElement>) => void;
  onChangeCategories: (scopeValues: ScopeValuesInterface[]) => void;
  onChangeProducts: (scopeValues: ScopeValuesInterface[]) => void;
  onChangeIsExternal: (value: boolean) => void;
  onChangeProgressiveDiscounts: (
    progressiveDiscounts: ProgressiveDiscount[],
  ) => void;
  onChangeBuyXPayY: (buyXPayY: BuyXPayY | null) => void;
  onChangePercentageOff: (percentageOff: number) => void;
  onChangeRewards: (rewards: Reward[]) => void;
  onChangePaymentOptions: (paymentOptions: PaymentOptionInfoDto[]) => void;
}

export function PromotionForm({
  mode,
  values,
  errors,
  isExternal,
  promotionId,
  setFieldValue,
  onChange,
  onChangeSelect,
  onChangeCategories,
  onChangeProducts,
  onChangeIsExternal,
  onChangeProgressiveDiscounts,
  onChangeBuyXPayY,
  onChangeRewards,
  onChangePercentageOff,
  onChangePaymentOptions,
}: Readonly<PromotionFormProps>) {
  const { data: discountTypes } = usePromotionTypesQuery();

  useEffect(() => {
    const discountTypeSelected = discountTypes?.find(
      (current) => current.type === values.promotionType,
    );
    setFieldValue(
      'discountForQuantity',
      discountTypeSelected?.isDiscountForQuantity || false,
    );
    onChangeIsExternal(!!discountTypeSelected?.external);
  }, [discountTypes, onChangeIsExternal, setFieldValue, values.promotionType]);

  useEffect(() => {
    if (values.startDate > values.endDate)
      setFieldValue('endDate', values.startDate);
  }, [setFieldValue, values.endDate, values.startDate]);

  useEffect(() => {
    const isPaymentOptionsSet =
      values.paymentOptions && values.paymentOptions.length > 0;

    if (!isPercentageOffPromotion(values) && isPaymentOptionsSet) {
      setFieldValue('paymentOptions', []);
    }
  }, [values, setFieldValue]);

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <NameCard
        values={values}
        onChange={onChange}
        disabled={isExternal}
        promotionId={promotionId}
      />

      <DiscountTypeCard
        values={values}
        errors={errors}
        onChangeSelect={onChangeSelect}
        onChangeProgressiveDiscounts={onChangeProgressiveDiscounts}
        onChangeBuyXPayY={onChangeBuyXPayY}
        onChangePercentageOff={onChangePercentageOff}
      />

      {values.promotionType === PromotionTypes.CROSS_SELLING && (
        <CrossSelling
          values={values}
          errors={errors}
          onChangeRewards={onChangeRewards}
        />
      )}

      {!isExternal && (
        <>
          {values.promotionType !== PromotionTypes.CROSS_SELLING && (
            <ApplyToCard
              mode={mode}
              values={values}
              errors={errors}
              onChange={onChange}
              onChangeCategories={onChangeCategories}
              onChangeProducts={onChangeProducts}
            />
          )}

          <ShowByTag tag={PROMOTIONS_GRANULAR_COMBINATION_TAG}>
            <GranularDiscountCombination values={values} onChange={onChange} />
          </ShowByTag>

          <LimitsCard
            values={values}
            errors={errors}
            onChange={onChange}
            onChangePaymentOptions={onChangePaymentOptions}
          />

          <ShowByTag tag={CUSTOM_PROMOTION_LABEL_ENABLED}>
            <CustomLabelCard
              values={values}
              onChange={onChange}
              setFieldValue={setFieldValue}
              errors={errors}
            />
          </ShowByTag>
        </>
      )}
    </Box>
  );
}
